import { useLocation, useRouter } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { PageLoader } from '@customerio/pluma-components/react';
import Journeys from '@/pages/journeys';
import { PlanGuard } from '@/components/guards';
import { BUILDER_PLAN_SLUG } from '@/services/auth/plan-slugs';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { ProtectedRoute } from '@/services/router/components/protected-route';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { generatePath } from '@/services/router/generate-path';
import { getWorkspaceIdFromUrl } from '@/utils/workspace-id';
import { useCurrentWorkspace } from '@/context/current-workspace';
import DataOutConnectorPage from './index.page';

const LOADING_LABEL = 'Loading integration…';

/**
 * The migrated URL drops the `journeys` segment; the legacy one is what the embedded Ember app
 * serves. The connector type is the same segment on both sides.
 */
function useHandoffHref(side: 'migrated' | 'legacy', type: string): string | null {
  const router = useRouter();
  const { pathname, searchStr, hash } = useLocation();
  const workspaceId = getWorkspaceIdFromUrl(pathname);

  if (workspaceId == null) {
    return null;
  }

  const to =
    side === 'migrated'
      ? '/workspaces/$workspaceId/integrations/data_out/$type'
      : '/workspaces/$workspaceId/journeys/integrations/data_out/$type';

  const href = router.buildLocation({ to, params: { workspaceId, type } }).href;
  // Ember transitions preserve query params, so both directions carry the query string and hash.
  return `${href}${searchStr}${hash === '' ? '' : `#${hash}`}`;
}

type DataOutHandoffProps = {
  type: string;
};

/**
 * The old `/journeys/integrations/data_out/:type` URL. Flag off → the embedded Ember app still owns
 * the page and renders here. Flag on → Hydra has claimed the URL purely to forward it to the
 * migrated route, so the directory cards and every other Ember-side link keep working without being
 * re-pointed.
 */
export function JourneysDataOutHandoff({ type }: DataOutHandoffProps) {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.DataOutIntegrationsMigration);
  const to = useHandoffHref('migrated', type);

  if (!isMigrated) {
    return <Journeys />;
  }

  // `/workspaces/last` is rewritten by a bridge-synced navigation that leaves route params stale,
  // so the id is read off the reactive location and held until it lands.
  if (to == null) {
    return <PageLoader label={LOADING_LABEL} />;
  }

  return <SafeNavigate to={to} options={{ shouldReplaceHistoryEntry: true }} />;
}

type DataOutRouteViewProps = DataOutHandoffProps & {
  children: ReactNode;
};

/**
 * The migrated `/integrations/data_out/:type` URL.
 *
 * Flag on → Hydra owns the page, applying the route-access gates Ember's `integrations` root route
 * did (data_out nests under it, so it inherits both): Builder-plan accounts go to the available-plans
 * page, and accounts missing `journeys/imports/view` are redirected away.
 *
 * Flag off → this URL has no Ember counterpart (Ember only knows the `journeys` form), so rendering
 * the embedded host here would 404 inside Ember. Hand back to the `journeys` twin instead, which
 * Ember does serve — the exact inverse of `JourneysDataOutHandoff`.
 */
function DataOutRouteView({ children, type }: DataOutRouteViewProps) {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.DataOutIntegrationsMigration);
  const legacyHref = useHandoffHref('legacy', type);
  const workspace = useCurrentWorkspace();

  if (!isMigrated) {
    if (legacyHref == null) {
      return <PageLoader label={LOADING_LABEL} />;
    }
    return <SafeNavigate to={legacyHref} options={{ shouldReplaceHistoryEntry: true }} />;
  }

  // Gate on the resolved workspace, not the URL segment: the page's reads go through
  // `requireWorkspaceId`, which throws while the workspaces list is still loading and while the
  // `/workspaces/last` sentinel leaves route params stale.
  if (workspace == null) {
    return <PageLoader label={LOADING_LABEL} />;
  }

  return (
    <PlanGuard excludedPlans={[BUILDER_PLAN_SLUG]}>
      <ProtectedRoute
        permission="journeys/imports/view"
        redirectTo={generatePath('/workspaces/:workspaceId', { workspaceId: String(workspace.id) })}
      >
        {children}
      </ProtectedRoute>
    </PlanGuard>
  );
}

export function DataOutConnectorRouteView({ type }: DataOutHandoffProps) {
  return (
    <DataOutRouteView type={type}>
      <DataOutConnectorPage type={type} />
    </DataOutRouteView>
  );
}
